import { ActionIcon, MultiSelect, Skeleton, Table, TextInput } from '@mantine/core'
import { showNotification } from '@ospace/shared'
import { IconDeviceFloppy, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import { FC, useState } from 'react'

import { useProductLines } from '../hooks'

export type Product = { name: string; productLines: string[]; id: string }

const Loader = () => (
  <>
    <td>
      <Skeleton height={18} radius='xl' />
    </td>
    <td>
      <Skeleton height={18} radius='xl' />
    </td>
    <td>
      <Skeleton height={18} radius='xl' />
    </td>
    <td>
      <Skeleton height={18} radius='xl' />
    </td>
  </>
)

const ProductsTable: FC<{
  products: Product[]
  postProduct: (product: Product) => void
  deleteProduct: (productId: string) => void
  updatingProductId?: string
}> = ({ products, postProduct, updatingProductId, deleteProduct }) => {
  const productLinesData = useProductLines()
  const [editProductName, setEditProductName] = useState('')
  const [editProductLines, setEditProductLines] = useState<string[]>([])
  const [editProduct, setEditProduct] = useState('')
  const [productLines, setProductLines] = useState<string[]>([])
  const [product, setProduct] = useState('')

  const updateHandler = (product: Product) => {
    if (products.some((p) => p.name === editProductName && p.id !== product.id)) {
      showNotification({
        message: 'Product name must be unique.',
        type: 'error',
      })
      return
    }
    setEditProduct('')
    postProduct({
      id: product.id,
      name: editProductName,
      productLines: editProductLines,
    })
  }

  const addHandler = () => {
    if (products.some((p) => p.name === product)) {
      showNotification({
        message: 'Product name must be unique.',
        type: 'error',
      })
      return
    }
    postProduct({
      name: product,
      productLines,
      id: crypto.randomUUID(),
    })
    setProduct('')
    setProductLines([])
  }
  return (
    <div className='w-xl-600px'>
      <Table verticalSpacing='xs' highlightOnHover>
        <Table.Thead>
          <Table.Tr key='header'>
            <Table.Th>Product</Table.Th>
            <Table.Th>Product lines</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {products.map((product: Product) => (
            <Table.Tr key={product.name}>
              {updatingProductId === product.id ? (
                <Loader />
              ) : (
                <>
                  <Table.Td>
                    {editProduct === product.id ? (
                      <TextInput
                        value={editProductName}
                        onChange={(e) => {
                          setEditProductName(e.target.value)
                        }}
                      />
                    ) : (
                      product.name
                    )}
                  </Table.Td>
                  <Table.Td>
                    <MultiSelect
                      searchable
                      readOnly={editProduct !== product.id}
                      value={editProduct !== product.id ? product.productLines : editProductLines}
                      onChange={setEditProductLines}
                      data={productLinesData.status === 'success' ? productLinesData.data : []}
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color={
                        editProduct === product.id && editProductName && editProductLines.length > 0
                          ? 'green'
                          : 'blue'
                      }
                    >
                      {editProduct === product.id &&
                      editProductName &&
                      editProductLines.length > 0 ? (
                        <IconDeviceFloppy
                          size='1.125rem'
                          onClick={() => {
                            updateHandler(product)
                          }}
                        />
                      ) : (
                        <IconEdit
                          size='1.125rem'
                          onClick={() => {
                            setEditProduct(product.id)
                            setEditProductName(product.name)
                            setEditProductLines(product.productLines)
                          }}
                        />
                      )}
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color='red'
                      onClick={() => {
                        deleteProduct(product.id)
                      }}
                    >
                      <IconTrash size='1.125rem' />
                    </ActionIcon>
                  </Table.Td>
                </>
              )}
            </Table.Tr>
          ))}
          {updatingProductId && !products.some((p) => p.id === updatingProductId) ? (
            <Table.Tr>
              <Loader />
            </Table.Tr>
          ) : (
            <Table.Tr>
              <Table.Td>
                <TextInput
                  aria-label='new-product-name'
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                />
              </Table.Td>
              <Table.Td>
                <MultiSelect
                  searchable
                  aria-label='new-product-lines'
                  value={productLines}
                  onChange={setProductLines}
                  data={productLinesData.status === 'success' ? productLinesData.data : []}
                />
              </Table.Td>
              <Table.Td>
                <ActionIcon
                  aria-label='add-product-button'
                  color='green'
                  disabled={!product || !productLines.length}
                  onClick={addHandler}
                >
                  <IconPlus size='1.125rem' />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </div>
  )
}

export { ProductsTable }
